import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, MessageSquare } from "lucide-react";

import { api } from "../api/client";
import { ACTIVITY_KINDS, ActivityForm } from "./ActivityForm";
import { Button, Card, EmptyState, cx } from "./ui";
import { formatDateTime, formatRelative } from "../lib/format";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import type { TimelineEntry } from "../api/types";

/**
 * The contact timeline (FR-5.4) and the form that writes to it.
 *
 * The feed is one flat list because the API returns one flat list. Bookings,
 * deals and invoices join it as those pillars land, and nothing here changes
 * when they do -- that is the whole point of the fixed entry shape.
 */
export function Timeline({ contactId }: { contactId: string }) {
  const [composing, setComposing] = useState(false);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["timeline", contactId],
    queryFn: ({ signal }) => api.timeline(contactId, signal),
  });

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Activity</h2>
        <Button variant={composing ? "ghost" : "primary"} onClick={() => setComposing((v) => !v)}>
          {composing ? "Cancel" : "Log activity"}
        </Button>
      </div>

      {composing ? (
        <ActivityForm
          onCancel={() => setComposing(false)}
          onSave={(body) =>
            api.logActivity(contactId, body).then((saved) => {
              void queryClient.invalidateQueries({ queryKey: ["timeline", contactId] });
              void queryClient.invalidateQueries({ queryKey: ["contact", contactId] });
              void queryClient.invalidateQueries({ queryKey: ["contacts"] });
              setComposing(false);
              return saved;
            })
          }
        />
      ) : null}

      <div className={cx(composing && "mt-5 border-t border-slate-100 pt-5")}>
        {query.isPending ? (
          <ul className="space-y-4">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex gap-3">
                <div className="size-7 shrink-0 animate-pulse rounded-full bg-slate-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/3 animate-pulse rounded bg-slate-200" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
                </div>
              </li>
            ))}
          </ul>
        ) : query.isError ? (
          <EmptyState title="Could not load the timeline" body="Try again in a moment." />
        ) : query.data && query.data.length > 0 ? (
          <ol className="relative space-y-5 border-l border-slate-200 pl-6">
            {query.data.map((entry) => (
              <TimelineRow key={`${entry.kind}-${entry.id}-${entry.occurred_at}`} entry={entry} />
            ))}
          </ol>
        ) : (
          <EmptyState
            icon={<Clock size={24} />}
            title="Nothing logged yet"
            body="Calls, emails, notes and tasks appear here in order."
          />
        )}
      </div>
    </Card>
  );
}

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  const meta = ACTIVITY_KINDS.find((k) => k.value === entry.type);
  const Icon = entry.kind === "system" ? Clock : (meta?.icon ?? MessageSquare);
  const timeZone = useAccountTimeZone();

  // The server's title carries a UTC-rendered label. Rebuilt here in the
  // account's zone when there is a structured time to build it from, so this
  // feed and the Leads card cannot disagree about when the customer said they
  // might come in.
  const title =
    entry.requested_start_at
      ? `${entry.title.split(" · tentative")[0]} · tentative ${formatDateTime(
          entry.requested_start_at,
          timeZone,
        )}`
      : entry.title;

  return (
    <li className="relative">
      <span
        className={cx(
          "absolute -left-[31px] flex size-7 items-center justify-center rounded-full ring-4 ring-white",
          entry.kind === "system"
            ? "bg-slate-100 text-slate-400"
            : entry.is_open_task
              ? "bg-amber-100 text-amber-700"
              : "bg-slate-900 text-white",
        )}
      >
        <Icon size={13} />
      </span>

      <div className="flex flex-wrap items-baseline gap-x-2">
        <p className="text-sm font-medium text-slate-900">{title}</p>
        {entry.is_open_task ? (
          <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
            Open
          </span>
        ) : null}
      </div>

      {entry.detail ? (
        <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-600">{entry.detail}</p>
      ) : null}

      <p className="mt-1 text-xs text-slate-400">
        <time
          dateTime={entry.occurred_at}
          title={formatDateTime(entry.occurred_at, timeZone)}
        >
          {formatRelative(entry.occurred_at)}
        </time>
        {entry.actor_name ? ` · ${entry.actor_name}` : ""}
        {meta ? ` · ${meta.label}` : ""}
      </p>
    </li>
  );
}

/**
 * The write form.
 *
 * Fields are revealed by kind rather than all shown at once: asking for a call
 * duration on a note is a small way of telling somebody the form was not
 * designed by anyone who logs calls.
 */
